import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TaxYearProvider } from './context/TaxYearContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import AccountantLayout from './components/AccountantLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Trips from './pages/Trips';
import Reports from './pages/Reports';
import PrintReport from './pages/PrintReport';
import Settings from './pages/Settings';
import MonthlyEarnings from './pages/MonthlyEarnings';
import Earnings from './pages/Earnings';
import Eligibility from './pages/Eligibility';
import Guide from './pages/Guide';
import Admin from './pages/Admin';
import InviteFriend from './pages/InviteFriend';
import Subscription from './pages/Subscription';
import MyAccountant from './pages/MyAccountant';
import Messages from './pages/Messages';
import Simulator from './pages/Simulator';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import CookieConsent from './components/CookieConsent';
import AccountantLogin from './pages/AccountantLogin';
import AccountantRegister from './pages/AccountantRegister';
import AccountantJoin from './pages/AccountantJoin';
import AccountantDashboard from './pages/accountant/AccountantDashboard';
import AccountantClientView from './pages/accountant/AccountantClientView';
import AccountantLinkClient from './pages/accountant/AccountantLinkClient';
import AccountantNotifications from './pages/accountant/AccountantNotifications';
import AccountantTeam from './pages/accountant/AccountantTeam';
import AccountantSettings from './pages/accountant/AccountantSettings';
import AccountantAdmin from './pages/accountant/AccountantAdmin';
import AccountantFeedback from './pages/accountant/AccountantFeedback';
import AccountantMessages from './pages/accountant/AccountantMessages';
import AccountantTaxReturn from './pages/accountant/AccountantTaxReturn';
import AccountantTaxWorkspace from './pages/accountant/AccountantTaxWorkspace';
import AccountantDocChecklist from './pages/accountant/AccountantDocChecklist';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect accountants to their portal (but not superadmin)
  if (user.role === 'accountant') {
    return <Navigate to="/accountant/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Protected route for accountant pages
const AccountantRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/accountant/login" replace />;
  }

  // Superadmin has access to everything
  if (user.role !== 'accountant' && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }
  
  return <AccountantLayout>{children}</AccountantLayout>;
};

// Protected route without Layout wrapper (for print view)
const ProtectedPrintRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'accountant') return <Navigate to="/accountant/dashboard" replace />;
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public route that allows accountants specifically
const AccountantPublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'accountant' || user.role === 'superadmin') return <Navigate to="/accountant/dashboard" replace />;
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
      <TaxYearProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
            <Route path="/trips" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
            <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
            <Route path="/monthly-earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/reports/print" element={<ProtectedPrintRoute><PrintReport /></ProtectedPrintRoute>} />
            <Route path="/eligibility" element={<ProtectedRoute><Eligibility /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><Guide /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/invite" element={<ProtectedRoute><InviteFriend /></ProtectedRoute>} />
            <Route path="/my-accountant" element={<ProtectedRoute><MyAccountant /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
            <Route path="/simulator" element={<ProtectedRoute><Simulator /></ProtectedRoute>} />
            <Route path="/privacy" element={<ProtectedRoute><PrivacyPolicy /></ProtectedRoute>} />
            <Route path="/terms" element={<ProtectedRoute><TermsOfService /></ProtectedRoute>} />
            <Route path="/refund" element={<ProtectedRoute><RefundPolicy /></ProtectedRoute>} />
            {/* Accountant Portal Routes */}
            <Route path="/accountant/login" element={<AccountantPublicRoute><AccountantLogin /></AccountantPublicRoute>} />
            <Route path="/accountant/register" element={<AccountantPublicRoute><AccountantRegister /></AccountantPublicRoute>} />
            <Route path="/accountant/join" element={<AccountantPublicRoute><AccountantJoin /></AccountantPublicRoute>} />
            <Route path="/accountant/dashboard" element={<AccountantRoute><AccountantDashboard /></AccountantRoute>} />
            <Route path="/accountant/client/:clientId" element={<AccountantRoute><AccountantClientView /></AccountantRoute>} />
            <Route path="/accountant/link-client" element={<AccountantRoute><AccountantLinkClient /></AccountantRoute>} />
            <Route path="/accountant/notifications" element={<AccountantRoute><AccountantNotifications /></AccountantRoute>} />
            <Route path="/accountant/team" element={<AccountantRoute><AccountantTeam /></AccountantRoute>} />
            <Route path="/accountant/settings" element={<AccountantRoute><AccountantSettings /></AccountantRoute>} />
            <Route path="/accountant/admin" element={<AccountantRoute><AccountantAdmin /></AccountantRoute>} />
            <Route path="/accountant/feedback" element={<AccountantRoute><AccountantFeedback /></AccountantRoute>} />
            <Route path="/accountant/messages" element={<AccountantRoute><AccountantMessages /></AccountantRoute>} />
            <Route path="/accountant/tax-return/:clientId" element={<AccountantRoute><AccountantTaxReturn /></AccountantRoute>} />
            <Route path="/accountant/tax-workspace/:clientId" element={<AccountantRoute><AccountantTaxWorkspace /></AccountantRoute>} />
            <Route path="/accountant/doc-checklist/:clientId" element={<AccountantRoute><AccountantDocChecklist /></AccountantRoute>} />
            {/* Public legal pages for non-logged-in visitors (Stripe + GDPR requirement) */}
            <Route path="/privacy-public" element={
              <div className="min-h-screen bg-slate-900 p-6"><PrivacyPolicy /></div>
            } />
            <Route path="/terms-public" element={
              <div className="min-h-screen bg-slate-900 p-6"><TermsOfService /></div>
            } />
            <Route path="/refund-public" element={
              <div className="min-h-screen bg-slate-900 p-6"><RefundPolicy /></div>
            } />
          </Routes>
          <CookieConsent />
          <Toaster position="bottom-center" richColors />
        </BrowserRouter>
      </TaxYearProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
